import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { genderLabel } from '../utils/format';

function MyProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', title: '', location: '', experience: '', education: '',
    skills: '', languages: '', summary: '', expectedSalary: '', jobTypes: 'overseas-market',
    contactEmail: '', contactPhone: '', linkedIn: '', isPublic: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [nickname, setNickname] = useState('');
  const [savedNickname, setSavedNickname] = useState('');
  const [gender, setGender] = useState('');
  const [savedGender, setSavedGender] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Load nickname & gender from user info
    api.get('/auth/me').then(res => {
      setNickname(res.data.nickname || '');
      setSavedNickname(res.data.nickname || '');
      setGender(res.data.gender || '');
      setSavedGender(res.data.gender || '');
    }).catch(() => {});

    api.get('/candidates/profile/me')
      .then(res => {
        if (res.data) {
          setForm({ ...res.data, isPublic: Boolean(res.data.isPublic) });
          setHasResume(true);
        } else {
          setForm(f => ({ ...f, name: user.displayName || user.username, contactEmail: user.email }));
          setHasResume(false);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleSaveNickname = async () => {
    setSavingNickname(true);
    try {
      await api.put('/auth/profile-basic', { nickname, gender });
      setSavedNickname(nickname || '');
      setSavedGender(gender || '');
      if (refreshUser) refreshUser();
      setToast('资料更新成功');
      setTimeout(() => setToast(''), 2000);
    } catch (err) {
      setMessage(err.response?.data?.error || '更新失败');
    }
    setSavingNickname(false);
  };

  const dirty = (nickname || '') !== (savedNickname || '') || (gender || '') !== (savedGender || '');
  const hasSaved = Boolean(savedNickname || savedGender);

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 60, textAlign: 'center' }}>
        <p>请先 <a href="/login" style={{ color: 'var(--primary)' }}>登录</a></p>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await api.post('/candidates/profile', form);
      setMessage('资料保存成功！');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || '保存失败');
    }
    setSaving(false);
  };

  const handleShareProfile = () => {
    // Get the candidate ID if it exists
    api.get('/candidates/profile/me').then(res => {
      if (res.data?.id) {
        const url = `${window.location.origin}/candidates/${res.data.id}`;
        navigator.clipboard.writeText(url);
        setToast('个人资料链接已复制');
        setTimeout(() => setToast(''), 2000);
      }
    });
  };

  const handleResumeParse = async () => {
    if (!resumeText.trim()) return;
    setParsing(true);
    setMessage('');
    try {
      const res = await api.post('/candidates/parse-resume', { rawText: resumeText });
      const parsed = res.data.parsed;
      setForm(prev => ({
        ...prev,
        ...Object.fromEntries(Object.entries(parsed).filter(([_, v]) => v)),
      }));
      setResumeText('');
      setMessage('简历解析完成，请检查并补充信息');
    } catch (err) {
      setMessage(err.response?.data?.error || '解析失败');
    }
    setParsing(false);
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="container" style={{ maxWidth: 700, paddingTop: 20, paddingBottom: 60 }}>
      <div className="page-header">
        <h1 className="page-title">个人资料</h1>
        <p className="page-subtitle">完善你的涉外人才档案，让招聘方更好地了解你</p>
      </div>

      {message && (
        <div className={message.includes('成功') ? 'error-message' : 'error-message'} style={message.includes('成功') ? { background: '#ecfdf5', color: '#059669' } : {}}>
          {message}
        </div>
      )}

      {/* 基础用户信息 */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>基础信息</h3>
        <p style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: 12 }}>
          昵称将在评论和导航栏中显示（最多 50 个字符）；性别在其他用户查看你时也会显示。
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <input
            className="form-input"
            value={nickname}
            onChange={e => setNickname(e.target.value)}
            placeholder="请输入昵称（可留空）"
            maxLength={50}
            style={{ flex: '1 1 200px', minWidth: 160 }}
          />
          {savedGender ? (
            <div className="form-input" style={{
              flex: '0 0 140px', background: 'var(--bg-secondary,#f6f7f9)',
              color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
              cursor: 'not-allowed',
            }} title="性别注册后不可修改">
              性别：{genderLabel(savedGender)}
            </div>
          ) : (
            <select
              className="form-select"
              value={gender}
              onChange={e => setGender(e.target.value)}
              style={{ flex: '0 0 160px' }}
              title="性别一旦保存将不可修改"
            >
              <option value="">选择性别（保存后不可改）</option>
              <option value="male">男</option>
              <option value="female">女</option>
              <option value="other">其他</option>
              <option value="unspecified">保密</option>
            </select>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveNickname}
            disabled={savingNickname || !dirty}
            style={{ flexShrink: 0 }}
          >
            {savingNickname ? '保存中...' : (hasSaved ? '变更昵称' : '保存资料')}
          </button>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 6 }}>
          账号用户名：{user.username}
          {savedGender ? '（性别已登记，不可修改）' : ''}
        </div>
      </div>

      {/* 简历入口 */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{ marginBottom: 4, fontSize: '1rem' }}>📄 我的简历</h3>
            <p style={{ color: 'var(--text-light)', fontSize: '0.85rem', margin: 0 }}>
              {hasResume
                ? '已创建简历。你可以展开查看并编辑完整信息。'
                : '你还没有创建简历。只有在你需要投递或希望被招聘方看到时再创建即可。'}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowResume(v => !v)}
            style={{ flexShrink: 0 }}
          >
            {showResume ? '收起简历' : (hasResume ? '编辑简历' : '创建简历')}
          </button>
        </div>
      </div>

      {showResume && (
      <>
      {/* Resume paste & parse */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>🤖 智能填充（可选）</h3>
        <p style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: 12 }}>
          粘贴你的简历或自我介绍，系统会自动解析并填充下方表单。
        </p>
        <textarea
          value={resumeText}
          onChange={e => setResumeText(e.target.value)}
          placeholder={`粘贴简历内容...\n\n例如：\n张三\n高级合规经理 | 8年涉外法律经验\n上海\n教育：北京大学法学硕士\n技能：跨境合规, GDPR, 反洗钱\n语言：中文母语, 英语流利\nemail: zhangsan@example.com`}
          style={{
            width: '100%', minHeight: 120, padding: 12, borderRadius: 8,
            border: '1px solid var(--border)', fontSize: '0.9rem',
            fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleResumeParse}
          disabled={parsing || !resumeText.trim()}
          style={{ marginTop: 8, width: '100%' }}
        >
          {parsing ? '解析中...' : '🔍 智能解析并填充'}
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>基本信息</h3>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">姓名 <span className="required">*</span></label>
              <input className="form-input" name="name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">当前职位</label>
              <input className="form-input" name="title" value={form.title || ''} onChange={handleChange} placeholder="如：高级合规经理" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">所在地</label>
              <input className="form-input" name="location" value={form.location || ''} onChange={handleChange} placeholder="如：上海、新加坡" />
            </div>
            <div className="form-group">
              <label className="form-label">工作经验</label>
              <input className="form-input" name="experience" value={form.experience || ''} onChange={handleChange} placeholder="如：8年涉外法律经验" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">个人简介</label>
            <textarea className="form-textarea" name="summary" value={form.summary || ''} onChange={handleChange}
              placeholder="简要介绍你的职业背景、核心优势和求职意向..." />
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>专业背景</h3>

          <div className="form-group">
            <label className="form-label">教育背景</label>
            <textarea className="form-textarea" name="education" value={form.education || ''} onChange={handleChange}
              placeholder="如：北京大学法学硕士、Columbia LLM" style={{ minHeight: 80 }} />
          </div>

          <div className="form-group">
            <label className="form-label">专业技能</label>
            <input className="form-input" name="skills" value={form.skills || ''} onChange={handleChange}
              placeholder="用逗号分隔，如：跨境合规,数据隐私,GDPR,反洗钱" />
            <div className="form-hint">多个技能请用逗号分隔</div>
          </div>

          <div className="form-group">
            <label className="form-label">语言能力</label>
            <input className="form-input" name="languages" value={form.languages || ''} onChange={handleChange}
              placeholder="如：中文母语、英语流利、日语N1" />
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>求职意向</h3>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">期望岗位类型</label>
              <select className="form-select" name="jobTypes" value={form.jobTypes || ''} onChange={handleChange}>
                <option value="">不限</option>
                <option value="overseas-market">国内负责海外市场</option>
                <option value="global-market">国内负责全球市场</option>
                <option value="foreign-domestic">国内外资企业</option>
                <option value="expat">外派</option>
                <option value="local-hire">海外本地</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">期望薪资</label>
              <input className="form-input" name="expectedSalary" value={form.expectedSalary || ''} onChange={handleChange}
                placeholder="如：50-70万/年" />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>联系方式与隐私</h3>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">邮箱</label>
              <input className="form-input" name="contactEmail" value={form.contactEmail || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">电话/微信</label>
              <input className="form-input" name="contactPhone" value={form.contactPhone || ''} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">LinkedIn</label>
            <input className="form-input" name="linkedIn" value={form.linkedIn || ''} onChange={handleChange}
              placeholder="LinkedIn 个人主页链接" />
          </div>

          <div className="form-group">
            <div className="toggle-group">
              <label className="toggle">
                <input type="checkbox" name="isPublic" checked={form.isPublic} onChange={handleChange} />
                <span className="toggle-slider"></span>
              </label>
              <span style={{ fontSize: '0.9rem' }}>公开资料（允许招聘方在人才广场浏览）</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" className="btn btn-primary btn-lg" style={{ flex: 1 }} disabled={saving}>
            {saving ? '保存中...' : '保存资料'}
          </button>
          <button type="button" className="btn btn-secondary btn-lg" onClick={handleShareProfile}>
            分享我的资料
          </button>
        </div>
      </form>

      {/* 删除账号 */}
      <div style={{ marginTop: 48, padding: 20, border: '1px solid #fecaca', borderRadius: 8, background: '#fef2f2' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#dc2626' }}>危险区域</h3>
        <p style={{ fontSize: '0.85rem', color: '#991b1b', margin: '0 0 16px' }}>
          删除账号后，你的所有数据（个人资料、发布的岗位、消息记录等）将被永久清除，无法恢复。
        </p>
        <button
          className="btn"
          style={{ background: '#dc2626', color: 'white', fontSize: '0.85rem' }}
          disabled={deleting}
          onClick={async () => {
            const confirmText = window.prompt('此操作不可逆！请输入"删除我的账号"来确认：');
            if (confirmText !== '删除我的账号') {
              if (confirmText !== null) alert('输入不正确，已取消操作');
              return;
            }
            setDeleting(true);
            try {
              await api.delete('/auth/account');
              alert('账号已删除，感谢你使用 RollSleeves');
              localStorage.removeItem('token');
              window.location.href = '/';
            } catch (err) {
              alert(err.response?.data?.error || '删除失败');
              setDeleting(false);
            }
          }}
        >
          {deleting ? '删除中...' : '永久删除我的账号'}
        </button>
      </div>
      </>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export default MyProfile;
